/**
 * Preferences Service
 * API client for user preferences endpoints
 */

import { api } from './api';
import { storage } from './storage';
import {
  DIMENSION_UNIT_DEFAULTS_KEY,
  fieldLevelDimensionUnits,
  parseDimensionUnits,
  type DimensionUnits,
} from '../request/dimensionUnitChoices';
import {
  DEFAULT_DIGEST_PREFERENCES,
  DIGEST_PREFERENCE_DEFAULTS,
  DIGEST_PREFERENCE_KEYS,
  parseDigestBoolean,
  parseDigestFrequency,
  parseStoredDigestBoolean,
} from '../config/digestPreferenceKeys';

import type {
  DigestBooleanPreferenceKey,
  DigestFrequency,
} from '../config/digestPreferenceKeys';

export interface PreferenceResponse {
  success: boolean;
  key: string;
  value: boolean | string;
}

// Supervisor region types
export type SupervisorRegion = 'NAM' | 'EMEA' | 'APAC';

// Preference keys for supervisor email notifications
export const SUPERVISOR_EMAIL_PREFERENCE_KEYS: Record<
  SupervisorRegion,
  string
> = {
  NAM: 'supervisor_email_notifications_nam',
  EMEA: 'supervisor_email_notifications_emea',
  APAC: 'supervisor_email_notifications_apac',
} as const;

// Global per-user email notifications switch (#1399).
// Opt-out model: absent/unreadable preference is treated as ON (send).
export const EMAIL_NOTIFICATIONS_ENABLED_KEY = 'email_notifications_enabled';

/**
 * Read one digest switch (#1507). The route answers with a JSON boolean; a
 * string form is tolerated on the way IN for parity with how the global switch
 * reads, and anything unreadable (404, odd value) falls back to the key's
 * default. Writes send a JSON boolean and nothing else.
 */
async function getDigestBoolean(
  key: DigestBooleanPreferenceKey,
): Promise<boolean> {
  try {
    const response = await api.get<PreferenceResponse>(`/preferences/${key}`);
    return (
      parseDigestBoolean(response.value) ??
      (typeof response.value === 'string'
        ? parseStoredDigestBoolean(response.value)
        : null) ??
      DIGEST_PREFERENCE_DEFAULTS[key]
    );
  } catch (error: any) {
    if (error.status === 404) {
      return DIGEST_PREFERENCE_DEFAULTS[key];
    }
    console.error(`Error getting ${key} preference:`, error);
    throw error;
  }
}

async function setDigestBoolean(
  key: DigestBooleanPreferenceKey,
  value: boolean,
): Promise<void> {
  try {
    await api.put<PreferenceResponse>(`/preferences/${key}`, { value });
  } catch (error) {
    console.error(`Error setting ${key} preference:`, error);
    throw error;
  }
}
const DIMENSION_UNIT_DEFAULTS_CACHE_KEY = 'dimension_unit_defaults_cache';

export class PreferencesService {
  /**
   * Get the retain_audio_ocr_files preference
   */
  static async getRetainAudioOcrFiles(): Promise<boolean> {
    try {
      const response = await api.get<PreferenceResponse>(
        '/preferences/retain_audio_ocr_files',
      );
      return response.value === true || response.value === 'true';
    } catch (error: any) {
      // If preference doesn't exist (404), return false (default)
      if (error.status === 404) {
        return false;
      }
      console.error('Error getting retain_audio_ocr_files preference:', error);
      throw error;
    }
  }

  /**
   * Set the retain_audio_ocr_files preference
   */
  static async setRetainAudioOcrFiles(value: boolean): Promise<void> {
    try {
      await api.put<PreferenceResponse>('/preferences/retain_audio_ocr_files', {
        value,
      });
    } catch (error) {
      console.error('Error setting retain_audio_ocr_files preference:', error);
      throw error;
    }
  }

  /**
   * Get all preferences
   */
  static async getAllPreferences(): Promise<Record<string, string>> {
    try {
      const response = await api.get<{
        success: boolean;
        preferences: Record<string, string>;
      }>('/preferences');
      return response.preferences || {};
    } catch (error) {
      console.error('Error getting all preferences:', error);
      throw error;
    }
  }

  /**
   * Get supervisor email notification preference for a region
   * Defaults to false (disabled) if not set
   */
  static async getSupervisorEmailNotification(
    region: SupervisorRegion,
  ): Promise<boolean> {
    try {
      const key = SUPERVISOR_EMAIL_PREFERENCE_KEYS[region];
      const response = await api.get<PreferenceResponse>(`/preferences/${key}`);
      return response.value === true || response.value === 'true';
    } catch (error: any) {
      // If preference doesn't exist (404), return false (default disabled - opt-in model)
      if (error.status === 404) {
        return false;
      }
      console.error(
        `Error getting ${region} supervisor email notification preference:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Set supervisor email notification preference for a region
   */
  static async setSupervisorEmailNotification(
    region: SupervisorRegion,
    value: boolean,
  ): Promise<void> {
    try {
      const key = SUPERVISOR_EMAIL_PREFERENCE_KEYS[region];
      await api.put<PreferenceResponse>(`/preferences/${key}`, { value });
    } catch (error) {
      console.error(
        `Error setting ${region} supervisor email notification preference:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Get the global email notifications preference (#1399).
   * Opt-out model: defaults to true (ON) when the preference is absent (404).
   */
  static async getEmailNotificationsEnabled(): Promise<boolean> {
    try {
      const response = await api.get<PreferenceResponse>(
        `/preferences/${EMAIL_NOTIFICATIONS_ENABLED_KEY}`,
      );
      // Only an explicit "false" disables; anything else is ON. Normalize the
      // string form (trim/case) to match server-side enforcement so the toggle
      // never displays ON while the server suppresses.
      const normalized =
        typeof response.value === 'string'
          ? response.value.trim().toLowerCase()
          : response.value;
      return !(normalized === false || normalized === 'false');
    } catch (error: any) {
      // Absent preference (404) means the user has never opted out.
      if (error.status === 404) {
        return true;
      }
      console.error(
        'Error getting email_notifications_enabled preference:',
        error,
      );
      throw error;
    }
  }

  /**
   * Set the global email notifications preference (#1399).
   */
  static async setEmailNotificationsEnabled(value: boolean): Promise<void> {
    try {
      await api.put<PreferenceResponse>(
        `/preferences/${EMAIL_NOTIFICATIONS_ENABLED_KEY}`,
        { value },
      );
    } catch (error) {
      console.error(
        'Error setting email_notifications_enabled preference:',
        error,
      );
      throw error;
    }
  }

  // ─── Digest emails (#1507) ───────────────────────────────────────────────

  /** Request changes email on/off. Default ON. */
  static getDigestRequestChangesEnabled(): Promise<boolean> {
    return getDigestBoolean(DIGEST_PREFERENCE_KEYS.requestChanges);
  }

  static setDigestRequestChangesEnabled(value: boolean): Promise<void> {
    return setDigestBoolean(DIGEST_PREFERENCE_KEYS.requestChanges, value);
  }

  /** Discussion messages email on/off. Default ON. */
  static getDigestCorrespondenceEnabled(): Promise<boolean> {
    return getDigestBoolean(DIGEST_PREFERENCE_KEYS.correspondence);
  }

  static setDigestCorrespondenceEnabled(value: boolean): Promise<void> {
    return setDigestBoolean(DIGEST_PREFERENCE_KEYS.correspondence, value);
  }

  /** Include requests the person oversees. Default OFF. Oversight roles only. */
  static getDigestIncludeOversight(): Promise<boolean> {
    return getDigestBoolean(DIGEST_PREFERENCE_KEYS.includeOversight);
  }

  static setDigestIncludeOversight(value: boolean): Promise<void> {
    return setDigestBoolean(DIGEST_PREFERENCE_KEYS.includeOversight, value);
  }

  /**
   * How often both digest emails may go out. Default `h1`. An unreadable or
   * unknown stored value shows the default rather than an empty picker.
   */
  static async getDigestFrequency(): Promise<DigestFrequency> {
    try {
      const response = await api.get<PreferenceResponse>(
        `/preferences/${DIGEST_PREFERENCE_KEYS.frequency}`,
      );
      // `allowMinute` here, because reading back a value the server itself
      // stored must not silently show the person something other than what
      // they chose. The server is the one that decided it was allowed; if it
      // later stops allowing it, the server's own read path falls back too.
      return (
        parseDigestFrequency(response.value, { allowMinute: true }) ??
        DEFAULT_DIGEST_PREFERENCES.frequency
      );
    } catch (error: any) {
      if (error.status === 404) {
        return DEFAULT_DIGEST_PREFERENCES.frequency;
      }
      console.error('Error getting digest_frequency preference:', error);
      throw error;
    }
  }

  /**
   * Sends the canonical token only. A caller holding a non-token string has a
   * bug; it is thrown here rather than sent to be refused with 400.
   */
  static async setDigestFrequency(value: DigestFrequency): Promise<void> {
    // Permissive here and authoritative at the route: the picker only offers
    // `m1` when the server said it may, and the route asks
    // `isDigestMinuteFrequencyAllowed()` again before storing. A second copy of
    // that decision in the client would be a second thing to get out of step.
    const frequency = parseDigestFrequency(value, { allowMinute: true });
    if (frequency === null) {
      throw new Error(`Invalid digest frequency: ${String(value)}`);
    }
    try {
      await api.put<PreferenceResponse>(
        `/preferences/${DIGEST_PREFERENCE_KEYS.frequency}`,
        { value: frequency },
      );
    } catch (error) {
      console.error('Error setting digest_frequency preference:', error);
      throw error;
    }
  }

  /** Chip defaults for the next new request. A failed fetch uses the last mix saved on this device. */
  static async getDimensionUnitDefaults(): Promise<DimensionUnits> {
    try {
      const response = await api.get<PreferenceResponse>(
        `/preferences/${DIMENSION_UNIT_DEFAULTS_KEY}`,
      );
      const parsed = parseDimensionUnits(response.value);
      if (Object.keys(parsed).length > 0) {
        await PreferencesService.rememberDimensionUnitDefaultsLocally(parsed);
      }
      return parsed;
    } catch {
      try {
        const cached = await storage.getJSON<unknown>(
          DIMENSION_UNIT_DEFAULTS_CACHE_KEY,
        );
        return parseDimensionUnits(cached);
      } catch {
        return {};
      }
    }
  }

  /** Remember the mix from a save so the next offline create opens on it. */
  static async rememberDimensionUnitDefaultsLocally(
    units: unknown,
  ): Promise<void> {
    const parsed = fieldLevelDimensionUnits(parseDimensionUnits(units));
    if (Object.keys(parsed).length === 0) return;
    try {
      await storage.setJSON(DIMENSION_UNIT_DEFAULTS_CACHE_KEY, parsed);
    } catch {
      // The request save still proceeds. The next online open refills the cache.
    }
  }
}
